import React, { useCallback, useEffect, useState, useMemo } from "react";
import { ReactFlowProvider, type Edge, type Node } from "reactflow";
import GraphCanvas from "components/Graph/GraphCanvas";
import type { GraphIssueNodeData } from "components/Graph/GraphIssueNodeTypes";
import type { GraphCanvasEdgeData } from "lib/api/graphCollections";
import {
  graphCollectionsApi,
  mapGraphSnapshotToCanvasData,
} from "lib/api/graphCollections";
import LoadingSkeleton from "ui/LoadingSkeleton";
import FullscreenViewer from "ui/FullscreenViewer";
import { graphMessage, type GraphLocale } from "./graphCopy";

export interface GraphFullscreenViewerProps {
  /** Whether the viewer is open. */
  isOpen: boolean;
  /** Called when the user dismisses the viewer (close button or Escape). */
  onClose: () => void;
  /** Collection id to load the snapshot for. */
  collectionId: number | string | null;
  /** Pre-fetched snapshot. Skips the network request when provided. */
  initialNodes?: Array<Node<GraphIssueNodeData>>;
  initialEdges?: Array<Edge<GraphCanvasEdgeData>>;
  initialRootNodeId?: string | null;
  /** Optional display title shown above the canvas. */
  title?: string | null;
  locale?: GraphLocale;
}

/**
 * Full-screen read-only graph viewer. Opens on top of the AppBar with a
 * higher z-index than `LAYER_TOKENS.appBar` so mobile users see just the
 * canvas + the close button — think Instagram-style "click a feed card,
 * explore the graph in fullscreen, swipe/close to return to the feed".
 *
 * Interactions allowed: pan/zoom, two-step edge highlight, node drag.
 * Interactions blocked: graph-collection edits (remove, connect, notes).
 */
const GraphFullscreenViewer: React.FC<GraphFullscreenViewerProps> = ({
  isOpen,
  onClose,
  collectionId,
  initialNodes,
  initialEdges,
  initialRootNodeId,
  title,
  locale = "en-US",
}) => {
  const [nodes, setNodes] = useState<Array<Node<GraphIssueNodeData>>>(() => initialNodes ?? []);
  const [edges, setEdges] = useState<Array<Edge<GraphCanvasEdgeData>>>(() => initialEdges ?? []);
  const [rootNodeId, setRootNodeId] = useState<string | null>(() => initialRootNodeId ?? null);
  const [isLoading, setIsLoading] = useState<boolean>(() =>
    isOpen && collectionId != null && !(initialNodes && initialNodes.length > 0)
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Sync with fresh initial data when the caller's cache updates.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (initialNodes && initialNodes.length > 0) {
      setNodes(initialNodes);
      setEdges(initialEdges ?? []);
      setRootNodeId(initialRootNodeId ?? null);
      setIsLoading(false);
    }
  }, [initialEdges, initialNodes, initialRootNodeId, isOpen]);

  // Fallback: fetch the snapshot ourselves if the caller didn't pass pre-
  // mapped data. This is the "click a thumbnail that still has its data in
  // its own provider" path; the thumbnail's copy is owned by a different
  // ReactFlowProvider, so we refetch into this viewer's own provider.
  useEffect(() => {
    if (!isOpen || !collectionId) {
      return;
    }
    if (initialNodes && initialNodes.length > 0) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setErrorMessage(null);
    (async () => {
      try {
        const snapshot = await graphCollectionsApi.getSnapshot(Number(collectionId));
        if (cancelled) {
          return;
        }
        const mapped = mapGraphSnapshotToCanvasData(snapshot);
        setNodes(mapped.nodes);
        setEdges(mapped.edges);
        setRootNodeId(mapped.rootNodeId ?? null);
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error("Failed to load graph snapshot for fullscreen viewer:", error);
        setErrorMessage(graphMessage(locale, "loadGraphError"));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [collectionId, initialNodes, isOpen, locale]);

  // The React Flow change handlers are no-ops because the viewer does not
  // persist layout changes. Kept as identity functions so the canvas's own
  // pan/drag machinery still works for the viewer session.
  const noop = useCallback(() => undefined, []);

  const resolvedTitle = useMemo(() => {
    const trimmed = typeof title === "string" ? title.trim() : "";
    return trimmed.length > 0 ? trimmed : graphMessage(locale, "untitledGraph");
  }, [locale, title]);

  return (
    <FullscreenViewer
      isOpen={isOpen}
      onClose={onClose}
      title={resolvedTitle}
      closeButtonPosition="left"
      className="bg-eoe-bg dark:bg-eoe-dark-bg"
      headerClassName="bg-eoe-bg dark:bg-eoe-dark-bg text-eoe-text dark:text-eoe-dark-text"
    >
      {isLoading ? (
        <LoadingSkeleton
          variant="graph"
          ariaLabel={graphMessage(locale, "loadingGraph")}
          containerClassName="graph-fullscreen-viewer-loading"
        />
      ) : errorMessage ? (
        <div
          className="flex h-full items-center justify-center px-6 text-center text-sm text-warning-red dark:text-warning-red-dark"
          role="alert"
        >
          {errorMessage}
        </div>
      ) : (
        <ReactFlowProvider>
          <GraphCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={noop}
            onEdgesChange={noop}
            depth={0}
            onDepthUp={noop}
            onDepthDown={noop}
            readOnly
            showDepthControls={false}
            locale={locale}
            rootNodeId={rootNodeId}
          />
        </ReactFlowProvider>
      )}
    </FullscreenViewer>
  );
};

export default GraphFullscreenViewer;
